import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import { Cloud, HardDrive, CheckCircle, XCircle, Settings } from 'lucide-react';

export default function StorageSettings() {
  const [config, setConfig] = useState({
    storageType: 'local', // 'local' or 'supabase'
    supabaseUrl: '',
    supabaseKey: ''
  });
  const [saved, setSaved] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem('sno_storage_config');
    if (saved) {
      setConfig(JSON.parse(saved));
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem('sno_storage_config', JSON.stringify(config));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const testConnection = async () => {
    if (config.storageType === 'local') {
      setTestResult({ success: true, message: 'Local storage is working!' });
      return;
    }

    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(config.supabaseUrl, config.supabaseKey);
      const { data, error } = await supabase.from('projects').select('count').limit(1);
      
      if (error) throw error;
      setTestResult({ success: true, message: 'Successfully connected to Supabase!' });
    } catch (error) {
      setTestResult({ success: false, message: `Connection failed: ${error.message}` });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Storage Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Storage Type Selection */}
        <div className="space-y-3">
          <Label>Storage Type</Label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setConfig(prev => ({ ...prev, storageType: 'local' }))}
              className={`p-4 border-2 rounded-lg text-left transition-colors ${
                config.storageType === 'local'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <HardDrive className={`w-6 h-6 mb-2 ${
                config.storageType === 'local' ? 'text-amber-600' : 'text-slate-400'
              }`} />
              <div className="font-semibold">Local Storage</div>
              <div className="text-xs text-slate-500 mt-1">
                Data stays on this device only
              </div>
            </button>

            <button
              onClick={() => setConfig(prev => ({ ...prev, storageType: 'supabase' }))}
              className={`p-4 border-2 rounded-lg text-left transition-colors ${
                config.storageType === 'supabase'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Cloud className={`w-6 h-6 mb-2 ${
                config.storageType === 'supabase' ? 'text-blue-600' : 'text-slate-400'
              }`} />
              <div className="font-semibold">Cloud Sync</div>
              <div className="text-xs text-slate-500 mt-1">
                Access from any device
              </div>
            </button>
          </div>
        </div>

        {/* Supabase Configuration */}
        {config.storageType === 'supabase' && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-sm text-blue-900 font-medium mb-3">
              Supabase Configuration
            </div>
            
            <div>
              <Label className="text-sm">Supabase Project URL</Label>
              <Input
                value={config.supabaseUrl}
                onChange={(e) => setConfig(prev => ({ ...prev, supabaseUrl: e.target.value }))}
                placeholder="https://xxxxx.supabase.co"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-sm">Supabase Anon Key</Label>
              <Input
                value={config.supabaseKey}
                onChange={(e) => setConfig(prev => ({ ...prev, supabaseKey: e.target.value }))}
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                className="mt-1"
                type="password"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={testConnection} variant="outline" size="sm">
                Test Connection
              </Button>
              <a
                href="https://supabase.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center"
              >
                Don't have Supabase? Sign up free →
              </a>
            </div>

            {testResult && (
              <Alert className={testResult.success ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}>
                <div className="flex items-start gap-2">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <AlertDescription className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                    {testResult.message}
                  </AlertDescription>
                </div>
              </Alert>
            )}
          </div>
        )}

        {/* Local Storage Info */}
        {config.storageType === 'local' && (
          <Alert className="border-amber-300 bg-amber-50">
            <AlertDescription className="text-amber-800 text-sm">
              <strong>Note:</strong> Projects are stored in your browser's local storage. 
              They won't be accessible from other devices or browsers. For multi-device access, 
              switch to Cloud Sync.
            </AlertDescription>
          </Alert>
        )}

        {/* Save Button */}
        <div className="flex items-center gap-3 pt-4 border-t">
          <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700">
            {saved ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Saved!
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
          {saved && (
            <span className="text-sm text-green-600">
              Settings saved! Refresh the page to apply changes.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
